import { Routes } from '@angular/router';
import { AppShellComponent } from '@campus/components';
import { Permissions, SettingsPermissions } from '@campus/dal';
import { AuthenticationGuard, CoupledTeacherGuard, LicenseGuard, PermissionGuard } from '@campus/guards';
import { AppResolver } from '../app/app.resolver';
import { AppHeaderComponent } from '../app/components/navigation/app-header.component';
import { AppRailComponent } from '../app/components/navigation/app-rail.component';
import { HomePageGuard } from '../app/guard/home-page.guard';
import { TermsPrivacyPopupGuard } from '../app/guard/terms-privacy-popup/terms-privacy-popup.guard';

export const devRoutes: Routes = [
  {
    path: 'v2/error',
    runGuardsAndResolvers: 'always',
    resolve: { isResolved: AppResolver },
    component: AppShellComponent,
    children: [
      { path: '', component: AppHeaderComponent, outlet: 'header' },

      {
        path: '',
        loadChildren: () => import('@campus/pages/error').then((m) => m.PagesErrorModule),
      },
    ],
  },
  {
    path: 'v2',
    runGuardsAndResolvers: 'always',
    canActivate: [AuthenticationGuard, TermsPrivacyPopupGuard],
    resolve: { isResolved: AppResolver },
    component: AppShellComponent,
    children: [
      {
        path: '',
        canActivate: [PermissionGuard],
        data: {
          fallBackPermissions: [SettingsPermissions.APP_SHELL_V2],
          fallbackUrl: '/home',
        },
        children: [
          { path: '', component: AppHeaderComponent, outlet: 'header' },
          {
            path: 'logout',
            loadChildren: () => import('@campus/pages/logout').then((m) => m.PagesLogoutModule),
          },
          {
            path: '',
            children: [
              { path: '', component: AppRailComponent, outlet: 'navigation-rail' },
              {
                path: '',
                redirectTo: 'home',
                pathMatch: 'full',
              },
              {
                path: 'home',
                loadChildren: () => import('@campus/pages/home').then((m) => m.PagesV2HomeModule),
                data: {
                  breadcrumbText: 'Dashboard',
                  headerDetails: {
                    title: 'Dashboard',
                    icon: 'home',
                  },
                },
                canActivate: [LicenseGuard, HomePageGuard],
              },
              {
                path: 'methods',
                loadChildren: () => import('@campus/pages/methods').then((m) => m.PagesV2MethodsModule),
                data: {
                  breadcrumbText: 'Methodes',
                  headerDetails: { title: 'Methodes', icon: 'methods' },
                  requiredPermissions: [`!${SettingsPermissions.HIDE_IN_ARCHIVE}`],
                },
                canActivate: [LicenseGuard, PermissionGuard],
              },
              {
                path: 'results',
                loadChildren: () => import('@campus/pages/resultsV2').then((m) => m.PagesResultsModule),
                data: {
                  breadcrumbText: 'Resultaten',
                  headerDetails: { title: 'Results', icon: 'results' },
                  requiredPermissions: [`!${SettingsPermissions.HIDE_IN_ARCHIVE}`],
                },
                canActivate: [LicenseGuard, PermissionGuard],
              },
              {
                path: 'resultsV2',
                loadChildren: () => import('@campus/pages/resultsV2').then((m) => m.PagesResultsV2Module),
                data: {
                  breadcrumbText: 'Resultaten',
                  headerDetails: { title: 'Results', icon: 'results' },
                  requiredPermissions: [
                    `!${SettingsPermissions.HIDE_IN_ARCHIVE}`,
                    Permissions.results.TEMP_USE_RESULT_VIEWMODEL_V2,
                  ],
                },
                canActivate: [LicenseGuard, PermissionGuard],
              },
              {
                path: 'tasks',
                loadChildren: () => import('@campus/pages/kabas-tasks').then((m) => m.PagesV2KabasTasksModule),
                data: {
                  breadcrumbText: 'Taken',
                  skip: true,
                  headerDetails: { title: 'Taken', icon: 'tasks' },
                  requiredPermissions: [`!${SettingsPermissions.HIDE_IN_ARCHIVE}`],
                },
                canActivate: [LicenseGuard, CoupledTeacherGuard, PermissionGuard], // TODO: still necessary?
              },
              {
                path: 'students',
                loadChildren: () => import('@campus/pages/students').then((m) => m.PagesStudentsModule),
                data: {
                  breadcrumbText: 'Studenten',
                  headerDetails: { title: 'Studenten', icon: 'students' },
                  requiredPermissions: [`!${SettingsPermissions.HIDE_IN_ARCHIVE}`],
                },
                canActivate: [PermissionGuard],
              },
              {
                path: 'bundles',
                loadChildren: () => import('@campus/pages/bundles').then((m) => m.PagesBundlesV2Module),
                data: {
                  breadcrumbText: 'Bundels',
                  headerDetails: { title: 'Bundels', icon: 'bundles' },
                  requiredPermissions: [`!${SettingsPermissions.HIDE_IN_ARCHIVE}`],
                },
                canActivate: [LicenseGuard, CoupledTeacherGuard, PermissionGuard],
              },
              {
                path: 'settings',
                loadChildren: () =>
                  import('@campus/pages/settings/dashboard').then((m) => m.PagesSettingsDashboardV2Module),
              },
              {
                path: 'search',
                loadChildren: () => import('@campus/pages/global-search').then((m) => m.PagesGlobalSearchV2Module),
                data: {
                  breadcrumbText: 'Zoekresultaten',
                  denseMenu: true,
                  headerDetails: { title: 'Zoeken', icon: 'magnifier' },
                  requiredPermissions: [`!${SettingsPermissions.HIDE_IN_ARCHIVE}`],
                },
                canActivate: [PermissionGuard],
              },
              {
                path: 'schools',
                loadChildren: () => import('@campus/pages/schools').then((m) => m.PagesSchoolsModule),
                data: {
                  breadcrumbText: 'Mijn scholen',
                  headerDetails: { title: 'Mijn scholen', icon: 'schools' },
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
